import { useCallback, useEffect, useRef, useState } from "react"

import useFocusWithin from "../../../hooks/useFocusWithin"
import useHover from "../../../hooks/useHover"
import { parseNumber } from "../../../utils/NumberUtils"
import { toHankakuNumber } from "../../../utils/StringUtils"
import type { NumberFieldOptions } from "./NumberFieldType"
import useComposing from "./useComposing"
import useSpin from "./useSpin"
import validateInputNumber from "./validateInputNumber"

const useNumberField = (options: NumberFieldOptions, readOnly: boolean) => {
  const { min, max, step, maxDecimalDigits, name = "", value = null, onUpdateValue } = options

  const elementRef = useRef<HTMLInputElement>(null)
  const currentValueRef = useRef(value?.toString() ?? "")
  const [ariaValueNow, setAriaValueNow] = useState<number>()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // 数値入力コンポーネントでの操作以外でvalueが変わった場合に、その値を反映します
  useEffect(() => {
    if (!elementRef.current) return
    if (parseNumber(elementRef.current.value, null) === value) return

    const valueAsString = value?.toString() ?? ""
    elementRef.current.value = valueAsString
    currentValueRef.current = valueAsString
    setAriaValueNow(value ?? undefined)
  }, [value])

  const selectionRef = useRef({ start: 0, end: 0 })
  const onSelect = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const node = e.currentTarget
    selectionRef.current.start = node.selectionStart ?? node.value.length
    selectionRef.current.end = node.selectionEnd ?? node.value.length
  }, [])

  const updateValue = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    (nextValue: string | ((current: string) => number), skipValidate: boolean = false) => {
      const element = elementRef.current
      if (!element) return

      const valueAsNumber =
        typeof nextValue === "function" ? nextValue(currentValueRef.current).toString() : toHankakuNumber(nextValue)

      const errorMessage = validateInputNumber(valueAsNumber, { min, max, maxDecimalDigits })

      if (skipValidate || errorMessage === null) {
        element.value = valueAsNumber
        currentValueRef.current = valueAsNumber
        setAriaValueNow(parseNumber(valueAsNumber, undefined))
        setErrorMessage(null)
        onUpdateValue?.(parseNumber(valueAsNumber, null), name)
      } else {
        // 不正な値が入力された場合、入力された値と選択範囲の状態を復元します。
        element.value = currentValueRef.current
        element.setSelectionRange(selectionRef.current.start, selectionRef.current.end)
        setErrorMessage(errorMessage)
      }
    },
    [min, max, maxDecimalDigits, onUpdateValue, name]
  )

  const { isComposing, onCompositionStart, onCompositionEnd } = useComposing(updateValue)
  const { onUp, onDown, onKeyDown } = useSpin({ min, max, step }, isComposing, readOnly, updateValue)

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isComposing) return

      const skipValidate =
        !(e.nativeEvent instanceof InputEvent) ||
        /**
         * 以下の3つの理由により、値の変更が消去による場合はバリデーションを行いません。
         * 1.コンポーネントにvalueを渡したときはバリデーションを行わないため、入力範囲外の値が入ってきて操作不可になる可能性があるため
         * 2.未知のバグで入力範囲外の値が入ってきた場合、操作不可になってしまうため
         * 3.ユーザーの行動を阻害しないため
         * ex) 入力値が999.9、最大整数桁数が3、最大小数桁数が1、999. と 9 の間にキャレットがある状態で、
         * Backspaceなどを押した場合入力値が9999となり最大整数桁数を超えるためバリデーションに失敗し、入力値が999.9に戻ってしまいます。
         * これは入力を消そうとしているユーザーの行動を阻害するため、入力内容を消すことに関しては制限を設けません。
         *
         * @see https://rawgit.com/w3c/input-events/v1/index.html#interface-InputEvent-Attributes
         */
        e.nativeEvent.inputType.startsWith("delete")

      updateValue(e.target.value, skipValidate)
    },
    [isComposing, updateValue]
  )

  const onFocusWithin = useCallback(() => {
    // 数値入力欄にフォーカスが当たった時、フォーカス対象がinputでない場合強制的にinputにフォーカスを移します。
    const element = elementRef.current
    if (element && document.activeElement !== element) {
      element.focus()
    }
  }, [])
  const onBlurWithin = useCallback(() => setErrorMessage(null), [])

  const { isFocusWithin, focusWithinProps } = useFocusWithin({ onFocusWithin, onBlurWithin })
  const { isHovered, hoverProps } = useHover()

  return {
    onUp,
    onDown,
    errorMessage,
    shouldShowTooltip: !!errorMessage,
    shouldShowSpinner: !readOnly && (isHovered || isFocusWithin),
    containerProps: { ...hoverProps, ...focusWithinProps },
    numberFieldProps: {
      ref: elementRef,
      type: "tel",
      value: undefined,
      onChange,
      onKeyDown,
      onCompositionStart,
      onCompositionEnd,
      onSelect,
      role: "spinbutton",
      "aria-valuemin": min,
      "aria-valuemax": max,
      "aria-valuenow": ariaValueNow,
    },
  }
}

export default useNumberField
